import EmptyState from '@/components/EmptyState';
import { FileTextIcon } from '@/icons';
import { errorToast, formatDate } from '@/utils';
import { Avatar, Button, Flex, Input, Skeleton } from 'antd';
import { useState } from 'react';
import {
  useApproveLeaveApplicationMutation,
  useGetAllLeaveApplicationsQuery,
  useRejectLeaveApplicationMutation,
} from '../../../../../../app/features/employee/emp-leave/emp-leave.api';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import SectionCard from '../../../../../../components/SectionCard';
import useAuth from '../../../../../../hooks/useAuth';
import useModal from '../../../../../../hooks/useModal';
import { toast } from '../../../../../../lib/toast';
import { firstChar } from '../../../../../../utils/string.utils';
import CalendarIcon from './_icons/CalendarIcon';

const { TextArea } = Input;

export default function QuickActionsCard() {
  const { organizationId, userId } = useAuth();
  const [activeId, setActiveId] = useState<string | null>();
  const [rejectComment, setRejectComment] = useState('');
  const {
    open: confirmOpen,
    handleOpen,
    handleClose: confirmClose,
  } = useModal<any, 'CONFIRM'>();

  // API calls
  const { data: leaveData, isLoading } = useGetAllLeaveApplicationsQuery({
    params: {
      organization_id: organizationId,
      status: 'PENDING',
      page: 1,
      limit: 100,
    },
  });

  const [approveLeave, { isLoading: isApproving }] =
    useApproveLeaveApplicationMutation();
  const [rejectLeave, { isLoading: isRejecting }] =
    useRejectLeaveApplicationMutation();

  // console.log("leaveData", leaveData);

  const handleApprove = async () => {
    const id = confirmOpen?.data?.id;
    if (!id) return;
    setActiveId(id);
    try {
      const response = await approveLeave({
        id: id,
        data: {
          approver_id: userId,
          comment: '',
        },
      }).unwrap();

      // Check if response has nested success/error structure
      if (response?.data?.success === false) {
        toast(
          'error',
          response.data.message || 'Failed to approve leave application',
        );
        return;
      }

      // If overall success is true, show success message
      if (response?.success === true) {
        toast('success', 'Leave application approved successfully');
        confirmClose();
      }
    } catch (error) {
      errorToast(error, 'Failed to approve leave application');
    } finally {
      setActiveId(null);
    }
  };

  const handleReject = async () => {
    const id = confirmOpen?.data?.id;
    if (!id) return;
    setActiveId(id);
    try {
      const response = await rejectLeave({
        id: id,
        data: {
          approver_id: userId,
          comment: rejectComment ? rejectComment : '',
        },
      }).unwrap();

      // Check if response has nested success/error structure
      if (response?.data?.success === false) {
        toast(
          'error',
          response.data.message || 'Failed to reject leave application',
        );
        return;
      }

      // If overall success is true, show success message
      if (response?.success === true) {
        toast('success', 'Leave application rejected successfully');
        setRejectComment('');
        confirmClose();
      }
    } catch (error) {
      errorToast(error, 'Failed to reject leave application');
    } finally {
      setActiveId(null);
    }
  };

  const handleConfirmSubmit = () => {
    if (confirmOpen.type === 'REJECT') {
      return handleReject();
    }

    if (confirmOpen.type === 'APPROVE') {
      return handleApprove();
    }
  };

  const handleConfirmClose = () => {
    setRejectComment(''); // ✅ clear comment
    confirmClose(); // existing close logic
  };

  const confirmationTextMap: Record<string, string> = {
    REJECT: 'You want to reject this leave application.',
    APPROVE: 'You want to approve this leave application.',
  };
  return (
    <SectionCard>
      <SectionCard.Title>quick actions</SectionCard.Title>

      {/* SCROLL WRAPPER */}
      <SectionCard.Content>
        {isLoading ? (
          <div className="space-y-5">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="p-5 rounded-xl border border-[#0F172A] bg-[#0F172A] transition-all duration-200 hover:border-[#FFFFFF]"
              >
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                  {/* LEFT SIDE */}
                  <div className="inline-block lg:flex gap-5">
                    <div className="flex justify-between gap-5 mb-3 lg:mb-0">
                      <Skeleton.Avatar
                        size={56}
                        shape="circle"
                        className="border-[0.86px] border-teal-500 rounded-full"
                      />
                      <div className="text-white text-sm font-medium block lg:hidden">
                        <Skeleton.Input size="small" style={{ width: 60 }} />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Skeleton.Input size="small" style={{ width: 150 }} />
                        <Skeleton.Input size="small" style={{ width: 100 }} />
                      </div>

                      <Skeleton.Input
                        size="small"
                        style={{ width: 120 }}
                        className="mb-1"
                      />
                      <Skeleton.Input size="small" style={{ width: 100 }} />
                    </div>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="text-left md:text-right">
                    <div className="text-white text-xl font-medium hidden lg:block mb-4">
                      <Skeleton.Input size="small" style={{ width: 80 }} />
                    </div>

                    <div className="flex gap-3 mt-4 justify-between lg:justify-end">
                      <Skeleton.Button style={{ width: 80 }} />
                      <Skeleton.Button style={{ width: 80 }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : leaveData?.data?.data?.length === 0 ? (
          <EmptyState
            className="py-5"
            icon={<FileTextIcon size={36} />}
            title="No Pending Leave Requests"
            description="There are no leave applications awaiting approval. New requests submitted by employees will appear here for your review."
          />
        ) : (
          leaveData?.data?.data?.map((item) => (
            <div
              key={item.id}
              className="p-5 rounded-xl border border-[#0F172A] bg-[#0F172A] 
               transition-all duration-200 hover:border-[#FFFFFF]"
            >
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
                {/* LEFT SIDE */}
                <div className="inline-block lg:flex gap-5 h-full">
                  <div className="flex justify-between gap-5 mb-3 lg:mb-0">
                    {(item.employee as any).photo ? (
                      <Avatar
                        className="w-14 h-14 rounded-[30px] border-[0.86px] border-teal-500"
                        src={(item.employee as any).photo || undefined}
                      />
                    ) : (
                      <Flex
                        justify="center"
                        align="center"
                        className="w-14 h-14 rounded-[30px] border-[0.86px] font-semibold text-lg border-teal-500"
                      >
                        {firstChar(item.employee.name)}
                      </Flex>
                    )}

                    <div className="text-white text-sm font-medium block lg:hidden">
                      {new Date(item.applied_at).toLocaleTimeString()}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white text-xl font-medium">
                        {item.employee.name}
                      </span>

                      <span className="bg-[#1E293B] text-white text-sm px-2.5 py-[5px] rounded-lg">
                        {item.leave_type} Leave
                      </span>
                    </div>

                    <div className=" text-white text-sm font-semibold flex justify-start items-center gap-2 !mt-[2px]">
                      {' '}
                      <CalendarIcon className="text-sm text-white" />{' '}
                      {formatDate(item?.start_date, 'MMM DD, YY')} -{' '}
                      {formatDate(item?.end_date, 'MMM DD, YY')}
                    </div>

                    <p className="text-white text-base font-semibold mt-4">
                      {item.employee.designation}
                    </p>

                    <p className="text-white text-base font-semibold">
                      Department : {item.employee.department || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* RIGHT SIDE */}
                <div className="text-left md:text-right flex flex-col justify-between h-full">
                  <div className="text-white text-xl font-medium hidden lg:block">
                    {new Date(item.applied_at).toLocaleTimeString()}
                  </div>

                  <div className="flex gap-3 mt-4 justify-between lg:justify-end">
                    <Button
                      className="!border-teal-500 !text-teal-500 !bg-[#1E293B]
                       !text-base !rounded-lg px-5 py-1 md:w-auto"
                      type="default"
                      loading={isRejecting && activeId === item.id}
                      onClick={() =>
                        handleOpen({
                          type: 'REJECT',
                          data: { id: item.id },
                        })
                      }
                    >
                      Reject
                    </Button>

                    <Button
                      className="!bg-teal-500 !text-neutral-700 !border-none
                       !text-base !rounded-lg px-5 py-1 md:w-auto"
                      type="primary"
                      loading={isApproving && activeId === item.id}
                      onClick={() =>
                        handleOpen({
                          type: 'APPROVE',
                          data: { id: item.id },
                        })
                      }
                    >
                      Approve
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </SectionCard.Content>

      {/* Confirmation Modal */}
      <ConfirmationModal
        data={confirmOpen.data}
        type={confirmOpen.type}
        handleClose={handleConfirmClose}
        handleSubmit={handleConfirmSubmit}
        open={
          (confirmOpen.open && confirmOpen.type === 'REJECT') ||
          (confirmOpen.open && confirmOpen.type === 'APPROVE')
        }
        loading={isRejecting || isApproving}
        title="Are you sure?"
        subTitle={confirmOpen.type ? confirmationTextMap[confirmOpen.type] : ''}
        buttonName="Confirm"
        disabled={confirmOpen.type === 'REJECT' && !rejectComment.trim()}
      >
        {confirmOpen.type === 'REJECT' && (
          <div className="flex flex-col gap-2 mt-5">
            <div className="text-white text-base font-bold">Reason</div>
            <TextArea
              rows={4}
              size="middle"
              placeholder="Write here..."
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              maxLength={100}
              className="!h-36 !rounded-xl"
            />
            <div className="text-gray-400 text-sm text-right">
              {rejectComment.length}/100
            </div>
          </div>
        )}
      </ConfirmationModal>
    </SectionCard>
  );
}
